/**
 * Inline PDF / image viewer for a GR invoice.
 *
 * Loads the invoice detail (to grab attachment IDs) and renders the first
 * attachment inline. PDFs render in an <iframe> using the download endpoint
 * with disposition=inline; images render in an <img>.
 *
 * If the invoice has no attachments, shows an empty state. If there are
 * multiple attachments, a simple selector allows the user to switch.
 */
import { FileText, Loader2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useGrInvoice } from "@/hooks/use-gr-invoices";

interface GrInvoicePdfViewerProps {
  invoiceId: string;
  /** Override classes on the viewer container. Defaults to h-full w-full. */
  className?: string;
}

function isImage(mimeType: string | null | undefined): boolean {
  if (!mimeType) return false;
  return mimeType.startsWith("image/");
}

export function GrInvoicePdfViewer({ invoiceId, className }: GrInvoicePdfViewerProps) {
  const { data, isLoading, isError } = useGrInvoice(invoiceId);
  const [selectedIdx, setSelectedIdx] = useState(0);

  const containerClass = className ?? "h-full w-full";

  if (isLoading) {
    return (
      <div
        className={`flex items-center justify-center rounded-md border bg-muted/20 ${containerClass}`}
      >
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div
        className={`flex flex-col items-center justify-center rounded-md border bg-muted/20 p-6 text-center ${containerClass}`}
      >
        <FileText className="mb-2 h-10 w-10 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">Could not load invoice.</p>
      </div>
    );
  }

  const attachments = data.attachments ?? [];

  if (attachments.length === 0) {
    return (
      <div
        className={`flex flex-col items-center justify-center rounded-md border bg-muted/20 p-6 text-center ${containerClass}`}
      >
        <FileText className="mb-2 h-10 w-10 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">No file attached to this invoice.</p>
      </div>
    );
  }

  const idx = Math.min(selectedIdx, attachments.length - 1);
  const selected = attachments[idx];
  if (!selected) {
    return (
      <div
        className={`flex flex-col items-center justify-center rounded-md border bg-muted/20 p-6 text-center ${containerClass}`}
      >
        <FileText className="mb-2 h-10 w-10 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">No file attached to this invoice.</p>
      </div>
    );
  }

  const inlineUrl = `/api/gr-invoices/${invoiceId}/download?attachmentId=${selected.id}&disposition=inline`;

  return (
    <div className={`flex flex-col overflow-hidden rounded-md border bg-white ${containerClass}`}>
      {attachments.length > 1 && (
        <div className="flex flex-wrap gap-1 border-b bg-muted/30 px-2 py-1">
          {attachments.map((att, i) => (
            <Button
              key={att.id}
              type="button"
              variant={i === idx ? "secondary" : "ghost"}
              size="sm"
              className="h-7 text-xs"
              onClick={() => setSelectedIdx(i)}
            >
              {att.fileName}
            </Button>
          ))}
        </div>
      )}
      <div className="flex-1 overflow-auto">
        {isImage(selected.mimeType) ? (
          <img
            src={inlineUrl}
            alt={selected.fileName}
            className="mx-auto block max-w-full object-contain"
          />
        ) : (
          <iframe
            key={selected.id}
            src={inlineUrl}
            title={selected.fileName}
            className="h-full w-full border-0"
          />
        )}
      </div>
    </div>
  );
}
